import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeDa from '@angular/common/locales/da';
import { ApplicationConfig, ErrorHandler, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteDefaultOptions } from '@angular/material/autocomplete';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_EXPANSION_PANEL_DEFAULT_OPTIONS, MatExpansionPanelDefaultOptions } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { appErrorCodes } from '@core/config/error-codes.const';
import { RoleEnum } from '@core/config/role.enum';
import { CookiePolicyService } from '@core/cookie/cookie-policy.service';
import { DATE_FORMATS } from '@core/date/util/date-format';
import { GlobalErrorHandlerService } from '@core/global-error-handler/global-error-handler.service';
import { WINDOW_REF } from '@core/tokens/window-ref-injection-token.const';
import { OAuthModuleConfig, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { AuthService, DEVELOPER_ROLE, OAUTH_STORAGE, PUBLIC_PAGES } from 'ok-angular-shared/auth';
import { APP_ERROR_CODES, ErrorMessageService, SnackbarService } from 'ok-angular-shared/components';
import { provideOKDateAdapter } from 'ok-angular-shared/date';
import { EnvironmentLoaderService } from 'ok-angular-shared/services';
import { AppSettings } from 'src/settings/appsettings.interface';
import { h401Interceptor } from 'src/interceptors/h401.interceptor';
import { snakeCaseInterceptor } from 'src/interceptors/snake_case.interceptor';
import { timeoutInterceptor } from 'src/interceptors/timeout.interceptor';
import { DEFAULT_TIMEOUT } from 'src/interceptors/tokens/timeout.interceptor.token';
import { versionHeaderInterceptor } from 'src/interceptors/version-header.interceptor';
import { routes } from './app.routes';
import { CustomIconService } from './services/custom-icon.service';
import { getDanishMatDatepickerIntl } from './services/datepicker-intl';
import { getAuthConfig } from './services/get-auth-config.fn';
import { getPaginatorIntl } from './services/paginator-intl';
import { clearTokensOnAuthConfigChange } from './utilities/clear-tokens-on-auth-config-change.fn';
import { showConfigError } from './utilities/show-config-error.fn';
import { stripAuth0PasswordResetParams } from './utilities/strip-auth0-password-reset-params.fn';
import { validateAppSettings } from './utilities/validate-app-settings.fn';

function initApp(
  authService: AuthService,
  customIconService: CustomIconService,
  cookiePolicyService: CookiePolicyService,
  storageService: OAuthStorage,
  appSettings: AppSettings
) {
  return async () => {
    stripAuth0PasswordResetParams();
    registerLocaleData(localeDa);

    const authConfig = getAuthConfig(appSettings.auth);
    clearTokensOnAuthConfigChange(authConfig, storageService);
    await authService.runInitialLoginSequence(authConfig, true);

    cookiePolicyService.init();
    customIconService.init();
  };
}

async function loadAppSettings(environmentLoaderService: EnvironmentLoaderService<AppSettings>): Promise<AppSettings> {
  try {
    const appSettings = await environmentLoaderService.load();
    validateAppSettings(appSettings);
    return appSettings;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Ukendt konfigurationsfejl';
    showConfigError(message);
    throw error;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    AuthService,
    SnackbarService,
    CustomIconService,
    ErrorMessageService,
    CookiePolicyService,
    EnvironmentLoaderService,
    provideAnimations(),
    provideRouter(routes),
    ...provideOKDateAdapter(DATE_FORMATS),
    provideHttpClient(
      withInterceptors([versionHeaderInterceptor, h401Interceptor, timeoutInterceptor, snakeCaseInterceptor]),
      withInterceptorsFromDi()
    ),
    provideZoneChangeDetection(),
    provideOAuthClient(),
    {
      // Overrides the OAuthModuleConfig registered by provideOAuthClient() above so the
      // resource server base URL is read from the runtime settings, at point-of-use, once
      // they've been loaded by the app initializer below. Guarded with optional chaining
      // because this runs for every request, including the appsettings.json fetch itself,
      // before settings have loaded.
      provide: OAuthModuleConfig,
      useFactory: (): OAuthModuleConfig => {
        const environmentLoaderService = inject<EnvironmentLoaderService<AppSettings>>(EnvironmentLoaderService);
        return {
          resourceServer: {
            sendAccessToken: true,
            customUrlValidation: (url: string) => {
              const baseUrl = environmentLoaderService.config?.appService?.baseUrl;
              return !!baseUrl && url.includes(baseUrl);
            },
          },
        };
      },
    },
    provideAppInitializer(() => {
      // inject() must be called synchronously here, before any `await`, so every dependency
      // is resolved up front and passed into the async initialization logic below.
      const environmentLoaderService = inject<EnvironmentLoaderService<AppSettings>>(EnvironmentLoaderService);
      const authService = inject(AuthService);
      const customIconService = inject(CustomIconService);
      const cookiePolicyService = inject(CookiePolicyService);
      const storageService = inject(OAUTH_STORAGE);

      return (async () => {
        const appSettings = await loadAppSettings(environmentLoaderService);
        const initializerFn = initApp(authService, customIconService, cookiePolicyService, storageService, appSettings);
        return initializerFn();
      })();
    }),
    { provide: LOCALE_ID, useValue: 'da-DK' },
    { provide: WINDOW_REF, useValue: window },
    { provide: DEFAULT_TIMEOUT, useValue: 30000 },
    { provide: APP_ERROR_CODES, useValue: appErrorCodes },
    { provide: MatPaginatorIntl, useFactory: getPaginatorIntl },
    { provide: MatDatepickerIntl, useFactory: getDanishMatDatepickerIntl },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: MAT_SELECT_CONFIG, useValue: { panelWidth: '' } },
    { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { disableClose: true, scrollStrategy: new NoopScrollStrategy(), maxWidth: '1200px' },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', color: 'accent', subscriptSizing: 'dynamic' } as MatFormFieldDefaultOptions,
    },
    {
      provide: MAT_EXPANSION_PANEL_DEFAULT_OPTIONS,
      useValue: { collapsedHeight: 'auto', expandedHeight: 'auto' } as MatExpansionPanelDefaultOptions,
    },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { autoActiveFirstOption: true } as MatAutocompleteDefaultOptions,
    },
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideIcon: 'true' },
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        touchGestures: 'auto',
        touchLongPressShowDelay: 0,
        touchendHideDelay: 3500,
      } as MatTooltipDefaultOptions,
    },
    {
      provide: DEVELOPER_ROLE,
      useValue: RoleEnum.SuperUser,
    },
    { provide: OAUTH_STORAGE, useValue: sessionStorage },
    // Routes that should be reachable without an authenticated session. Add public routes here.
    { provide: PUBLIC_PAGES, useValue: [] },
  ],
};
